import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Panel, PanelHeader, PanelBody } from '../../../../components/panel/panel';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import * as formik from 'formik';
import Swal from 'sweetalert2';
import axiosClient from '../../../../config/axios.js';
import Loader from '../../../../components/loader/index.js';
import { updateOperatorFields } from '../../../../services/OperatorService.js';
import { getOperatorSchema } from '../../../../validations/OperatorSchema.js';

const FIGURE_TYPE_DESCRIPTIONS = {
    '01': 'Operador',
    '02': 'Propietario',
    '03': 'Arrendador',
    '04': 'Notificado',
    '05': 'Integrante de Coordinados',
};

function EditOperator() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { Formik } = formik;
    const [loading, setLoading] = useState(true);

    const [initialValues, setInitialValues] = useState({
        name: '',
        first_surname: '',
        second_surname: '',
        figure_type: '',
        figure_type_description: '',
        figure_rfc: '',
        license_number: '',
    });

    useEffect(() => {
        setLoading(true);
        axiosClient.get(`/users/${id}`)
            .then(response => {
                const user = response.data;
                setInitialValues({
                    name: user.name || '',
                    first_surname: user.first_surname || '',
                    second_surname: user.second_surname || '',
                    figure_type: user.figure_type || '',
                    figure_type_description: user.figure_type_description || (user.figure_type ? FIGURE_TYPE_DESCRIPTIONS[user.figure_type] || '' : ''),
                    figure_rfc: user.figure_rfc || '',
                    license_number: user.license_number || '',
                });
            })
            .catch(error => {
                console.error('Error al cargar datos del operador:', error);
                const message = error.response?.data?.message || 'Error al cargar los datos del operador';
                Swal.fire({
                    title: 'Error',
                    text: message,
                    icon: 'error',
                    confirmButtonText: 'Cerrar',
                });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [id]);

    if (loading) {
        return <Loader isLoading={true} />;
    }

    return (
        <div>
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
                        <li className="breadcrumb-item"><Link to="/settings/operators">Operadores</Link></li>
                        <li className="breadcrumb-item active">Edición de Operador</li>
                    </ol>
                    <h1 className="page-header mb-0">Edición de Operador</h1>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-xl-12">
                    <Panel>
                        <PanelHeader>Datos de Carta Porte del Operador</PanelHeader>
                        <PanelBody>
                            <Formik
                                enableReinitialize
                                validationSchema={getOperatorSchema()}
                                onSubmit={(values, { setSubmitting }) => {
                                    setSubmitting(false);
                                    const payload = {
                                        figure_type: values.figure_type,
                                        figure_type_description: values.figure_type_description || FIGURE_TYPE_DESCRIPTIONS[values.figure_type] || '',
                                        figure_rfc: values.figure_rfc ? values.figure_rfc.trim().toUpperCase() : '',
                                        license_number: values.license_number ? values.license_number.trim() : '',
                                    };

                                    updateOperatorFields(id, payload)
                                        .then(() => {
                                            Swal.fire({
                                                title: 'Cambios guardados',
                                                text: 'Los datos del operador se han actualizado exitosamente',
                                                icon: 'success',
                                                confirmButtonText: 'Cerrar',
                                                showCancelButton: false,
                                            });
                                            navigate('/settings/operators');
                                        })
                                        .catch(err => {
                                            const message = err.response?.data?.message || (typeof err.response?.data === 'string' ? err.response.data : 'Error al actualizar operador');
                                            Swal.fire({
                                                title: 'Ups!',
                                                text: message,
                                                icon: 'error',
                                                confirmButtonText: 'Cerrar',
                                                showCancelButton: false,
                                            });
                                        });
                                }}
                                initialValues={initialValues}
                            >
                                {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => (
                                    <Form noValidate onSubmit={handleSubmit}>
                                        <Row className="mb-3">
                                            <Form.Group as={Col} md="4">
                                                <Form.Label>Nombre</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="name"
                                                    placeholder="Nombre"
                                                    value={values.name}
                                                    disabled
                                                    readOnly
                                                />
                                            </Form.Group>
                                            <Form.Group as={Col} md="4">
                                                <Form.Label>Primer apellido</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="first_surname"
                                                    placeholder="Primer apellido"
                                                    value={values.first_surname}
                                                    disabled
                                                    readOnly
                                                />
                                            </Form.Group>
                                            <Form.Group as={Col} md="4">
                                                <Form.Label>Segundo apellido</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="second_surname"
                                                    placeholder="Segundo apellido"
                                                    value={values.second_surname}
                                                    disabled
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Row>

                                        <Row className="mb-3">
                                            <Form.Group as={Col} md="4">
                                                <Form.Label>Tipo de figura <span className="text-danger">*</span></Form.Label>
                                                <Form.Select
                                                    name="figure_type"
                                                    value={values.figure_type}
                                                    onChange={(e) => {
                                                        const selected = e.target.value;
                                                        setFieldValue('figure_type', selected);
                                                        setFieldValue('figure_type_description', FIGURE_TYPE_DESCRIPTIONS[selected] || '');
                                                    }}
                                                    isInvalid={touched.figure_type && !!errors.figure_type}
                                                >
                                                    <option value="">Seleccione una opción</option>
                                                    <option value="01">01 - Operador</option>
                                                    <option value="02">02 - Propietario</option>
                                                    <option value="03">03 - Arrendador</option>
                                                    <option value="04">04 - Notificado</option>
                                                    <option value="05">05 - Integrante de Coordinados</option>
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.figure_type}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group as={Col} md="4">
                                                <Form.Label>RFC <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="figure_rfc"
                                                    placeholder="RFC de la figura"
                                                    value={values.figure_rfc}
                                                    maxLength={13}
                                                    onChange={(e) => {
                                                        setFieldValue('figure_rfc', e.target.value.toUpperCase());
                                                    }}
                                                    isInvalid={touched.figure_rfc && !!errors.figure_rfc}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.figure_rfc}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group as={Col} md="4">
                                                <Form.Label>No. de licencia <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="license_number"
                                                    placeholder="Número de licencia"
                                                    value={values.license_number}
                                                    onChange={handleChange}
                                                    isInvalid={touched.license_number && !!errors.license_number}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.license_number}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Row>

                                        <div className="d-flex justify-content-end gap-2 mt-4">
                                            <Link to="/settings/operators" className="btn btn-default">
                                                Cancelar
                                            </Link>
                                            <Button type="submit" variant="primary">
                                                Guardar cambios
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </PanelBody>
                    </Panel>
                </div>
            </div>
        </div>
    );
}

export default EditOperator;
